/**
 * Decide **se** e **quanto** comprimir um vídeo para caber no teto do canal.
 *
 * Módulo puro: sem DOM, sem ffmpeg, sem rede. É aqui que mora a regra de não
 * comprimir à toa — "dinâmico" significa que um vídeo que já cabe sai intacto, e
 * um que não cabe perde só o necessário para caber.
 *
 * O que NÃO se decide aqui: remuxar vs. reencodar. Isso depende dos codecs
 * DENTRO do arquivo, que só se sabe depois de abri-lo — quem decide é o worker,
 * que já tem o ffmpeg na mão. Este módulo decide o orçamento de bytes.
 */

/** Fração do teto que miramos. A Meta mede o arquivo FINAL, e o overhead do
 *  container mp4 (moov, índices) não é previsível ao bit — mirar no teto exato é
 *  perder por alguns KB depois de minutos de encode. */
export const FRACAO_ALVO = 0.9;

/** AAC 64 kbps mono/estéreo é transparente o bastante para fala, que é o que
 *  trafega num atendimento. Subir isso rouba bits do vídeo. */
export const BITRATE_AUDIO = 64_000;

/** Abaixo disto o vídeo vira mosaico: melhor recusar e explicar do que entregar
 *  algo ilegível depois de minutos de encode. */
export const PISO_BITRATE_VIDEO = 120_000;

/** Nunca ampliar: o teto de largura é só um limite superior. */
const LARGURA_MAXIMA_PADRAO = 1920;

/**
 * Degraus de resolução por orçamento de bitrate. Comprimir muito mantendo
 * 1080p produz borrão em movimento; reduzir a resolução dá uma imagem limpa no
 * mesmo número de bits.
 */
const DEGRAUS: readonly { bitrateMinimo: number; larguraMaxima: number }[] = [
  { bitrateMinimo: 1_500_000, larguraMaxima: LARGURA_MAXIMA_PADRAO },
  { bitrateMinimo: 700_000, larguraMaxima: 1280 },
  { bitrateMinimo: 350_000, larguraMaxima: 854 },
  { bitrateMinimo: 0, larguraMaxima: 640 },
];

export interface EntradaDoPlano {
  /** Tamanho do arquivo de origem, em bytes. */
  tamanhoBytes: number;
  /** Duração em segundos. Sem ela não há como calcular bitrate. */
  duracaoSec: number;
  /** Teto do canal, em bytes (ver `limiteVideoMeta`). */
  limiteBytes: number;
  /** Dimensões da origem, quando conhecidas — só para não ampliar. */
  larguraPx?: number | null;
  alturaPx?: number | null;
  /** Container de origem; mp4 que já cabe dispensa qualquer processamento. */
  mime?: string | null;
}

export type PlanoDeCompressao =
  | { acao: 'nao-precisa'; motivo: string }
  | {
      acao: 'converter';
      alvoBytes: number;
      bitrateVideo: number;
      bitrateAudio: number;
      /** `null` = preserva a resolução de origem. */
      larguraMaxima: number | null;
      motivo: string;
    }
  | { acao: 'impossivel'; motivo: string };

const ehMp4 = (mime?: string | null): boolean =>
  (mime ?? '').split(';').at(0)?.trim().toLowerCase() === 'video/mp4';

export function planejarCompressao(entrada: EntradaDoPlano): PlanoDeCompressao {
  const { tamanhoBytes, duracaoSec, limiteBytes, larguraPx, mime } = entrada;

  // 1. Já cabe e já está em mp4 → não encosta. É o caso mais comum e o mais
  //    importante de acertar: reencodar aqui só degradaria o vídeo e gastaria
  //    minutos do atendente.
  if (tamanhoBytes <= limiteBytes && ehMp4(mime)) {
    return { acao: 'nao-precisa', motivo: 'já cabe no limite e já é mp4' };
  }

  // 2. Cabe, mas o container não serve → o worker vai tentar remuxar (sem
  //    reencodar) e só reencoda se os codecs internos não servirem. O orçamento
  //    continua sendo o teto, para o caso de ele precisar reencodar.
  // 3. Não cabe → precisa reencodar dentro do orçamento.

  if (!Number.isFinite(duracaoSec) || duracaoSec <= 0) {
    return {
      acao: 'impossivel',
      motivo: 'não foi possível ler a duração do vídeo para calcular a compressão',
    };
  }

  const alvoBytes = Math.floor(limiteBytes * FRACAO_ALVO);
  const bitrateTotal = Math.floor((alvoBytes * 8) / duracaoSec);
  const bitrateVideo = bitrateTotal - BITRATE_AUDIO;

  if (bitrateVideo < PISO_BITRATE_VIDEO) {
    return {
      acao: 'impossivel',
      motivo:
        `o vídeo é longo demais para caber no limite: caberia a ${Math.max(0, Math.floor(bitrateVideo / 1000))} kbps, ` +
        `abaixo dos ${PISO_BITRATE_VIDEO / 1000} kbps mínimos para a imagem continuar legível`,
    };
  }

  const degrau =
    DEGRAUS.find((d) => bitrateVideo >= d.bitrateMinimo) ?? DEGRAUS[DEGRAUS.length - 1];

  // Nunca ampliar: se a origem já é menor que o degrau, preserva.
  const larguraMaxima =
    typeof larguraPx === 'number' && larguraPx > 0
      ? Math.min(larguraPx, degrau.larguraMaxima)
      : degrau.larguraMaxima;

  const preservaResolucao =
    typeof larguraPx === 'number' && larguraPx > 0 && larguraMaxima >= larguraPx;

  return {
    acao: 'converter',
    alvoBytes,
    bitrateVideo,
    bitrateAudio: BITRATE_AUDIO,
    larguraMaxima: preservaResolucao ? null : larguraMaxima,
    motivo:
      tamanhoBytes > limiteBytes
        ? `${Math.round(tamanhoBytes / 1024 / 1024)} MB acima do limite`
        : 'container não aceito pela Meta',
  };
}
